<script>
import baseMixin from '@/mixins/baseMixin'
import PromiseDialogDto from '@/models/dialog/promiseDialogDto'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'

export default {
  name: 'PromiseConfirmDialog',
  mixins: [baseMixin],
  props: {
    width: {
      type: String,
      default: '500',
    },
  },
  data() {
    return {
      showDialog: false,
      resolve: null,
      /**
       * @type {PromiseDialogDto}
       */
      options: null,
    }
  },
  computed: {
    hasOptions() {
      return !!this.options
    },
    isPersistent() {
      if (!this.hasOptions) return false
      return this.options?.persistent
    },
    hasIcon() {
      if (!this.hasOptions) return false
      return !!this.options?.icon
    },
    hasColor() {
      if (!this.hasOptions) return false
      return !!this.options?.color
    },
    hasActions() {
      if (!this.hasOptions) return false
      return !isNullOrEmptyArray(this.options?.actions)
    },
  },
  methods: {
    async show(opts) {
      this.options = opts || new PromiseDialogDto({})
      return new Promise((resolve) => {
        this.resolve = resolve
        this.showDialog = true
      })
    },

    resolveEvent(value) {
      this.showDialog = false
      this.resolve && this.resolve(value)
    },
  },
}
</script>

<template>
  <v-dialog
    v-model="showDialog"
    :width="width"
    :persistent="isPersistent"
    data-testid="promise-confirm-dialog"
  >
    <v-card v-if="hasOptions" class="rounded-xl">
      <!-- ICON -->
      <div class="dialog-content">
        <slot name="icon">
          <div
            v-if="hasIcon"
            class="mb-4"
            :class="options.iconClass ? options.iconClass : 'icon-warning'"
          >
            <v-icon :color="hasColor ? options.color : ''" large>
              {{ options.icon }}
            </v-icon>
          </div>
        </slot>

        <!-- TITLE -->
        <slot name="title">
          <v-card-text
            class="text-subtitle-1 pa-0 pb-2 text--dark font-weight-medium"
          >
            {{ options.title }}
          </v-card-text>
        </slot>

        <!-- BODY TEXT -->
        <slot name="text">
          <v-card-text class="pa-0 text-subtitle-description">
            {{ options.text }}
          </v-card-text>
        </slot>
      </div>

      <!-- ACTION BUTTONS -->
      <v-card-actions v-if="hasActions" class="justify-end mt-7 mb-0 pa-0">
        <BaseButton
          v-for="action in options.actions"
          :key="action.key"
          v-bind="action"
          :text="action && action.textBtn"
          :outlined="action && action.outlined"
          :light="action && action.light"
          :color="action && action.color"
          :width="action && action.width"
          :height="action && action.height"
          :border-radius="action && action.borderRadius"
          :class="action && action.class"
          :hover-color="action && action.hoverColor"
          :text-color="action && action.textColor"
          :prepend-icon="action.icon"
          @click="resolveEvent(action.event)"
        >
          {{ action.text }}
        </BaseButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
.v-card {
  min-height: 248px;
  padding: 24px;
  border-radius: 12px !important;
}
.dialog-content {
  width: 496px;
  min-height: 124px;

  .icon-warning {
    width: 48px;
    height: 48px;
    text-align: center;
    background: $color-warning-100;
    border: 8px solid;
    border-color: $color-warning-50;
    border-radius: 28px;

    .v-icon {
      top: 1.9px;
      font-size: 20px !important;
      color: $color-warning-600;
      border-width: 2px;
    }
  }

  .text-subtitle-1 {
    font-size: 18px !important;
    font-weight: 500 !important;
    line-height: 28px;
    color: $color-midnight-gray !important;
    letter-spacing: 0 !important;
  }
  .text-subtitle-description {
    line-height: 20px;
    color: $color-graphite !important;
    letter-spacing: 0;
  }
}
.cancel-btn {
  font-size: 16px !important;
  font-weight: 500;
  line-height: 24px !important;
  color: $color-graphite !important;
  letter-spacing: 0 !important;
  border-color: $color-white !important;
}
.discard-btn {
  font-size: 16px !important;
  font-weight: 500;
  line-height: 24px !important;
  color: $color-steel-gray !important;
  letter-spacing: 0 !important;
  border-color: $color-slate !important;
  box-shadow: 0 1px 2px 0 $color-black-transparent-3 !important;
}
.save-btn {
  font-size: 16px !important;
  font-weight: 500;
  line-height: 24px !important;
  letter-spacing: 0 !important;
  box-shadow: 0 1px 2px 0 $color-black-transparent-3 !important;
}
</style>
